import { Inject, Injectable } from '@angular/core';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  DataNoRowOverlayComponent,
  DateClientSideFilterComponent,
  DurationCellRendererComponent,
  FormatValueConversionCellRendererComponent,
  GroupComparator,
  GroupDistributionAgg,
  HeaderWithIconTemplate,
  TimeAgoCellRendererComponent,
} from '@y42/ag-grid';
import { ListViewColDef, ListViewGridMessages, QuickFilterModel } from '@y42/core/list-view';
import { FEATURE_TYPE } from '@y42/feature';
import { agGridJobIcon, dataTestIcon } from '@y42/feature/icons';
import {
  EntryGroupCellRendererComponent,
  TagsCellRendererComponent,
  TagsCellRendererParams,
} from '@y42/feature/shared/list-view';
import { importIcon } from '@y42/icons';
import { ImportWriteType, JobStatus, TriggeredByType, Y42FeatureType } from '@y42/json-schema';
import { QuickFilterLocationEnum } from '@y42/model';
import { formatSizeUnits } from '@y42/util/format-size-units';
import {
  GetQuickFilterTextParams,
  GridOptions,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { JobListViewData } from '../../../state';
import { JobParentData, JobParentType } from '../../../state/job-parent';
import { ImportTypeCellRendererComponent } from '../../ag-grid/import-type-cell-renderer/import-type-cell-renderer.component';
import { JobStatusCellRendererComponent } from '../../ag-grid/job-status-cell-renderer/job-status-cell-renderer.component';
import { JobStatusValueFormatter } from '../../ag-grid/job-status-value-formatter';
import { TriggeredByTypeCellRendererComponent } from '../../ag-grid/triggered-by-type-cell-renderer/triggered-by-type-cell-renderer.component';
import { JOB_PARENT_TYPE } from '../../services/job-parent-type.injection-token';

@Injectable()
export class JobListViewContentAgGridConfigService {
  constructor(
    @Inject(FEATURE_TYPE) private readonly featureType: Y42FeatureType,
    @Inject(JOB_PARENT_TYPE) private readonly jobParentType: JobParentType,
  ) {}

  readonly messages: ListViewGridMessages = {
    noDataHeader: 'No jobs found',
  };
  readonly quickFilterKey = QuickFilterLocationEnum.ImportHistory;

  readonly frameworkComponents: GridOptions['components'] = {
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
    entryGroupRenderer: EntryGroupCellRendererComponent,
    dateFilter: DateClientSideFilterComponent,
    // dataTestStatusRenderer: DataTestStatusCellRendererComponent,
    tags: TagsCellRendererComponent,
    // selectionRenderer: SelectionRendererComponent,
    triggeredByTypeRenderer: TriggeredByTypeCellRendererComponent,
    importTypeRenderer: ImportTypeCellRendererComponent,
    timeAgoCellRendererComponent: TimeAgoCellRendererComponent,
    durationCellRendererComponent: DurationCellRendererComponent,
    formatValueConversionCellRendererComponent: FormatValueConversionCellRendererComponent,
  };

  readonly columnDefs: Array<ListViewColDef<JobListViewData>> = [
    {
      headerName: 'Job ID',
      colId: 'request_id',
      field: 'request_id',
      minWidth: 130,
      filter: 'agTextColumnFilter',
      order: 10,
    },
    this.statusColumnConfig({
      headerName: this.isExport ? 'Status' : 'Final Status',
      field: 'status',
      colId: 'status',
      cellRenderer: JobStatusCellRendererComponent,
      minWidth: 110,
      order: 20,
    }),
    {
      headerName: 'Started',
      colId: 'created_at',
      field: 'created_at',
      cellRenderer: 'timeAgoCellRendererComponent',
      sort: 'desc',
      minWidth: 130,
      order: 30,
    },
    {
      headerName: 'Duration',
      aggFunc: 'sum',
      enableValue: true,
      filter: 'agNumberColumnFilter',
      minWidth: 130,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRenderer: 'durationCellRendererComponent',
      valueGetter: (params: ValueGetterParams<JobListViewData>) => {
        const job = params.data;
        if (!job) {
          return undefined;
        }

        const created_at = job.created_at;
        const finished_at = job.finished_at;
        return {
          startedAt: created_at,
          finishedAt: finished_at,
        };
      },
      order: 40,
    },
    {
      headerName: 'Mode',
      colId: 'write_disposition',
      field: 'details.write_disposition',
      hide: this.isExport,
      aggFunc: 'distribution',
      comparator: GroupComparator,
      filter: 'agSetColumnFilter',
      cellRenderer: 'importTypeRenderer',
      enableRowGroup: true,
      getQuickFilterText: (params: GetQuickFilterTextParams<JobListViewData, ImportWriteType>) => {
        if (params.value === ImportWriteType.Append) {
          return 'Incremental';
        } else {
          return 'Full';
        }
      },
      filterParams: {
        valueFormatter: (params: ValueFormatterParams<unknown, ImportWriteType>) => {
          if (params.value === ImportWriteType.Append) {
            return 'Incremental';
          } else {
            return 'Full';
          }
        },
      },
      order: 50,
    },
    this.statusColumnConfig({
      headerName: 'Import Status',
      field: 'import_status',
      colId: 'import_status',
      headerComponentParams: {
        template: HeaderWithIconTemplate(agGridJobIcon),
      },
      hide: this.isExport,
      cellRenderer: JobStatusCellRendererComponent,
      order: 60,
    }),
    this.statusColumnConfig({
      headerName: 'Data Tests Status',
      field: 'tests_status',
      colId: 'tests_status',
      headerComponentParams: {
        template: HeaderWithIconTemplate(dataTestIcon),
      },
      hide: this.isExport,
      cellRenderer: 'dataTestStatusRenderer',
      order: 70,
    }),
    {
      headerName: 'Triggered By',
      enableRowGroup: true,
      filter: 'agSetColumnFilter',
      colId: 'triggered_by_type',
      field: 'request.context.triggered_by_type',
      cellRenderer: 'triggeredByTypeRenderer',
      aggFunc: 'distribution',
      comparator: GroupComparator,
      minWidth: 90,
      maxWidth: 160,
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === TriggeredByType.User) {
            return 'User';
          } else if (params.value === TriggeredByType.Orchestration) {
            return 'Orchestration';
          }
          return 'n/a';
        },
      },
      order: 80,
    },
    {
      headerName: 'Total Rows',
      colId: 'row_count',
      aggFunc: 'avg',
      filter: 'agNumberColumnFilter',
      cellRenderer: 'formatValueConversionCellRendererComponent',
      valueGetter: (params) => {
        return params.data?.details.row_count !== undefined ? { number: params.data.details.row_count } : undefined;
      },
      minWidth: 100,
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      order: 90,
    },
    {
      headerName: 'New Rows',
      colId: 'current_import_row_count',
      hide: this.isExport,
      aggFunc: 'avg',
      filter: 'agNumberColumnFilter',
      cellRenderer: 'formatValueConversionCellRendererComponent',
      valueGetter: (params) => {
        return params.data?.details.current_import_row_count !== undefined
          ? { number: params.data.details.current_import_row_count, prefix: '+' }
          : undefined;
      },
      minWidth: 100,
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      order: 100,
    },
    {
      headerName: 'Size',
      colId: 'size_in_bytes',
      field: 'details.size_in_bytes',
      aggFunc: 'avg',
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => formatSizeUnits(params.value),
      minWidth: 100,
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      order: 110,
    },
    {
      headerName: 'Type',
      colId: 'datasourceTypeName',
      field: 'datasourceTypeName',
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      cellRenderer: (params: { data: unknown; node: IRowNode }) => {
        const eDiv = document.createElement('div');
        const data: JobListViewData = params.node.group ? params.node.allLeafChildren[0]?.data : params.data;

        if (data.datasourceTypeName) {
          eDiv.innerHTML = `<div class="truncate"><img width="15" class="mr-2 relative" style="top: -1px" src="${data.datasourceTypeLogo}"/>${data.datasourceTypeName}</div>`;
        } else if (!params.node.group) {
          eDiv.innerHTML = '<div>--- Deleted ---</div>';
        }
        return eDiv;
      },
      order: 120,
    },
    {
      headerName: this.isExport ? `Export` : `Data Source`,
      colId: 'datasourceName',
      field: 'datasourceName',
      enableRowGroup: true,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => {
        if (params.node!.group) {
          return params.value;
        }

        return params.value ?? '--- Deleted ---';
      },
      order: 130,
    },
    {
      headerName: 'Tags',
      colId: 'tags',
      field: 'tags',
      minWidth: 140,
      cellRenderer: 'tags',
      cellRendererParams: {
        showWhenIsNotGroup: true,
      } as TagsCellRendererParams,
      filter: 'agSetColumnFilter',
      filterValueGetter: (params) => {
        const data = params.data;
        const values: string[] = data?.tags ?? [];
        if (values) {
          return values;
        } else {
          return;
        }
      },
      order: 150,
    },
    {
      headerName: JobParentData[this.jobParentType].displayName.plural,
      colId: 'datasourceTableName',
      field: 'datasourceTableName',
      enableRowGroup: true,
      filter: 'agTextColumnFilter',
      headerComponentParams: {
        template: HeaderWithIconTemplate(JobParentData[this.jobParentType].logo),
      },
      valueFormatter: (params) => {
        if (params.node!.group) {
          return params.value;
        }

        return params.value ?? '--- Deleted ---';
      },
      order: 160,
    },
  ];

  readonly gridOptions: GridOptions = {
    groupDisplayType: 'multipleColumns',
    defaultColDef: {
      flex: 1,
      sortable: true,
      resizable: true,
      filterParams: {
        suppressAndOrCondition: true,
      },
      minWidth: 100,
    },
    suppressCellFocus: true,
    autoGroupColumnDef: {
      minWidth: 190,
      filter: 'agTextColumnFilter',
      flex: 1,
    },
    rowGroupPanelShow: 'always',
    getContextMenuItems: () => this.getContextMenuItems(),
    /*    rowClassRules: {
      'row-active': (params) => this.highlightActiveRows(params),
      'import-active': (params) => this.highlightActiveImport(params),
    },*/
    aggFuncs: {
      distribution: GroupDistributionAgg,
    },
    rowSelection: 'single',
  };

  readonly defaultQuickFilters: QuickFilterModel[] = [
    {
      id: 'invalid_import',
      default: false,
      name: 'Failed Imports',
      editable: false,
      filterModel: {
        status: {
          values: [JobStatus.Invalid],
          type: 'equals',
        },
      },
      is_font_awesome: true,
      icon: 'times',
      background_color: '#F36331',
    },
    {
      id: 'triggered_by_user',
      default: false,
      name: 'Triggered By User',
      editable: false,
      filterModel: {
        triggered_by_type: {
          values: [TriggeredByType.User],
          type: 'equals',
        },
      },
      is_font_awesome: true,
      icon: 'user',
      background_color: '#1b1b52',
    },
    {
      id: 'full_import',
      default: false,
      name: 'Full Imports',
      editable: false,
      filterModel: {
        write_disposition: {
          values: [ImportWriteType.Truncate],
          type: 'equals',
        },
      },
      is_font_awesome: false,
      icon: importIcon,
      background_color: '#f4f4fb',
    },
    {
      id: 'incremental_import',
      default: false,
      name: 'Incremental Imports',
      editable: false,
      filterModel: {
        write_disposition: {
          values: [ImportWriteType.Append],
          type: 'equals',
        },
      },
      is_font_awesome: false,
      icon: importIcon,
      background_color: '#f4f4fb',
    },
    {
      id: 'source_type_view',
      name: 'Source Type View',
      filterModel: {},
      default: false,
      editable: true,
      background_color: '#f4f4fb',
      icon: 'database',
      is_font_awesome: true,
      columnStates: [
        {
          colId: 'ag-Grid-AutoColumn-datasourceTypeName',
          width: 190,
          hide: false,
          pinned: 'left',
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'created_at',
          width: 130,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'write_disposition',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'state',
          width: 90,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'triggered_by_type',
          width: 90,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'row_count',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'current_import_row_count',
          hide: false,
          width: 100,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceTypeName',
          width: 100,
          hide: true,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: true,
          rowGroupIndex: 0,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'invalid_rows',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceName',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'tags',
          width: 140,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceTableName',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'request_id',
          width: 130,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
      ],
    },
    {
      id: 'table_view',
      name: 'Table View',
      filterModel: {},
      default: false,
      editable: true,
      background_color: '#f4f4fb',
      icon: JobParentData[this.jobParentType].logo as IconName,
      is_font_awesome: false,
      columnStates: [
        {
          colId: 'ag-Grid-AutoColumn-datasourceTableName',
          width: 190,
          hide: false,
          pinned: 'left',
          sort: 'desc',
          sortIndex: 0,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'created_at',
          width: 130,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'write_disposition',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'status',
          width: 90,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'triggered_by_type',
          width: 90,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'row_count',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'current_import_row_count',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceTypeName',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'invalid_rows',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceName',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'tags',
          width: 140,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceTableName',
          width: 100,
          hide: true,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: true,
          rowGroupIndex: 0,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'request_id',
          width: 130,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
      ],
    },
    {
      id: 'source_table_view',
      name: 'Source + Table View',
      filterModel: {},
      default: false,
      editable: true,
      background_color: '#f4f4fb',
      icon: 'layer-group',
      is_font_awesome: true,
      columnStates: [
        {
          colId: 'ag-Grid-AutoColumn-datasourceName',
          width: 190,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'ag-Grid-AutoColumn-datasourceTableName',
          width: 190,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceTypeName',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceTableName',
          width: 100,
          hide: true,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: true,
          rowGroupIndex: 1,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'created_at',
          width: 130,
          hide: false,
          pinned: null,
          sort: 'desc',
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'tags',
          width: 140,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'write_disposition',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'distribution',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'state',
          width: 90,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'distribution',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'triggered_by_type',
          width: 90,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'distribution',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'row_count',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'datasourceName',
          width: 100,
          hide: true,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: true,
          rowGroupIndex: 0,
          pivot: false,
          pivotIndex: null,
          flex: 1,
        },
        {
          colId: 'current_import_row_count',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'invalid_rows',
          width: 100,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: 'avg',
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
        {
          colId: 'request_id',
          width: 130,
          hide: false,
          pinned: null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        },
      ],
    },
  ];

  private get isExport() {
    return this.featureType === Y42FeatureType.Export;
  }

  private statusColumnConfig(options: Partial<ListViewColDef<JobListViewData>>): ListViewColDef<JobListViewData> {
    return {
      enableRowGroup: true,
      cellRenderer: JobStatusCellRendererComponent,
      filter: 'agSetColumnFilter',
      minWidth: 90,
      maxWidth: 160,
      getQuickFilterText: JobStatusValueFormatter,
      filterParams: {
        valueFormatter: JobStatusValueFormatter,
      },
      aggFunc: 'distribution',
      comparator: GroupComparator,
      ...options,
    };
  }

  private getContextMenuItems() {
    return [
      'copy',
      'expandAll',
      'contractAll',
      'export',
      'autoSizeAll',
      {
        name: 'Open in New Tab',
        action: () => {
          throw new Error('not yet implemnented');
          // let data: GenericMappedSubEntryModel<DatasourceTypeEnum>;
          // if (params.node.group) {
          //   data = params.node.allLeafChildren[0].data.mappedSubEntry;
          // } else {
          //   data = params.node.data.mappedSubEntry;
          // }
          // this.redirect(data);
        },
        cssClasses: ['redFont', 'bold'],
        /*icon: '<img src="../images/skills/windows.png"/>',*/
      },
    ];
  }
}
